import React from "react";
//#region
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { DocumentDraftFormTypes } from "models/formTypes";
import { WintSuperkollAPIModelCustomFilters } from "wint-api-client/dist/types";
import { ReportConstants, TableConstants } from "utils/constants";
//#endregion
//#region components
import {
  DashboardButtonGroup,
  DialogActionButton,
  FilterButton,
  PageLayouts,
  ReloadEvent,
  TableContextProvider,
  useCheckRelations,
} from "components/common";
import { useDocumentDraftService } from "serviceHooks";
import DocumentDraftListView from "./listView";
import useDocumentDraftFilterDefinition from "./forms/useDocumentDraftFilterDefinition";
import DocumentEmailBanner from "./banners/documentEmailBanner";
import { Grid } from "@mui/material";
import SelfInvoiceEmailBanner from "./banners/selfInvoiceEmailBanner";
//#endregion

//#region services
import useCreateDocumentDialog from "../forms/useCreateDialog";
import useReceiptPermissions from "components/receipt/usePermissions";
import useGenericDocumentPermissions from "../genericDocument/hooks/useGenericDocumentPermissions";
import useIncomingInvoicePermissions from "components/incomingInvoice/usePermissions";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const DocumentDraftReport: React.FC = () => {
  const documentDraftService = useDocumentDraftService();
  const loggedInUserWintEmail = useSelector((store: AppState) => store.auth.selectedCompany.loggedInUserWintEmail);
  const isSelfInvoicingEnabled = useSelector((store: AppState) => store.auth.selectedCompany.isSelfInvoicingEnabled);
  const createDocumentDialog = useCreateDocumentDialog;
  const { receiptCreator } = useReceiptPermissions();
  const { canCreateGenericDocuments } = useGenericDocumentPermissions();
  const { canCreateIncomingInvoices } = useIncomingInvoicePermissions();
  const { getIsReadonlyUser } = useCheckRelations();
  const isDocumentCreationApplicable = receiptCreator || canCreateIncomingInvoices || canCreateGenericDocuments;
  const isReadonlyUser = getIsReadonlyUser();

  const {
    filterFormFields: reportFilterDefinition,
    filterQueryStateHandler,
    apiToReportFilterFormatter,
  } = useDocumentDraftFilterDefinition();

  return (
    <ReloadEvent
      uniqueKey={ReportConstants.ReportKeys.DocumentDraft}
      render={(reloadKey, reload) => (
        <PageLayouts.DashboardLayout<DocumentDraftFormTypes.Filter>
          entityKey={0}
          title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.DocumentDraft.ReceivedDocuments)}
          topRightButtons={
            <DashboardButtonGroup
              buttons={[
                ...(!isReadonlyUser && isDocumentCreationApplicable
                  ? ([
                      <DialogActionButton
                        size="sm"
                        actionDialog={createDocumentDialog}
                        reload={reload}
                      >
                        {LocalizationHelperInstance.getFormattedMessage(
                          TranslationKeys.Document.AddDocument
                        )}
                      </DialogActionButton>,
                      ACTION_SEPARATOR as typeof ACTION_SEPARATOR,
                    ])
                  : []),
                <FilterButton />,
              ]}
            />          
          }
          filterFields={reportFilterDefinition}
          filterQueryStateHandler={filterQueryStateHandler}
          filterCategory={WintSuperkollAPIModelCustomFilters.FilterCategory.DocumentDraft}
          reportToAPIFilterFormatter={documentDraftService.reportToAPIFilter}
          apiToReportFilterFormatter={apiToReportFilterFormatter}
        >
          <Grid container direction="row" columnGap={{ lg: 4, md: 3, sm: 2 }}>
            <Grid item xs>
              <DocumentEmailBanner
                userEmail={loggedInUserWintEmail ?? ""}
                useFullWidth={!isSelfInvoicingEnabled}
              ></DocumentEmailBanner>
            </Grid>
            {isSelfInvoicingEnabled ? (
              <Grid item lg={4} xs={6}>
                <SelfInvoiceEmailBanner />
              </Grid>
            ) : (
              <></>
            )}
          </Grid>

          <TableContextProvider tableKey={TableConstants.DocumentDraftList}>
            <DocumentDraftListView reloadKey={reloadKey} reloadDocumentDrafts={reload} />
          </TableContextProvider>
        </PageLayouts.DashboardLayout>
      )}
    />
  );
};

export default DocumentDraftReport;
